import { Button, Input, Label, Skeleton, TOUCH, useDebouncedValue } from "@nymbl/nos2-rules";
import { TypeChip } from "@/components/time/TypeChip";
import { useAssignable } from "@/hooks/use-time-assignable";
import { projectLine } from "@/lib/time/labels";
import { cn } from "@/lib/utils";
import type { AssignableTask } from "@/schemas/time";

/** How many matches the picker lists; "Showing N of M" says when there are more. */
const SEARCH_LIMIT = 20;

/**
 * One task in a picker: name, client and project, type. `showDemand` adds the demand, for a task
 * the person reaches through two assignments, so the two can be told apart.
 */
export function TaskOption({
  task,
  showDemand,
  onPick,
}: {
  task: AssignableTask;
  showDemand: boolean;
  onPick: (task: AssignableTask) => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      className="h-auto min-h-12 w-full justify-between gap-3 rounded-none px-3 py-2 text-left whitespace-normal"
      onClick={() => onPick(task)}
    >
      <span className="min-w-0">
        <span className="block font-semibold break-words">{task.taskName}</span>
        <span className="block text-xs font-normal break-words text-muted-foreground">
          {projectLine(task.projectName, task.accountName)}
          {showDemand && task.demandName ? ` · ${task.demandName}` : ""}
        </span>
      </span>
      <TypeChip type={task.type} />
    </Button>
  );
}

interface TaskSearchProps {
  query: string;
  onQueryChange: (query: string) => void;
  onPick: (task: AssignableTask) => void;
  /** The search box's id; one per picker when several are on a page. */
  inputId?: string;
  label?: string;
}

/** One search over every task the person can log to (task, project or client name). */
export function TaskSearch({
  query,
  onQueryChange,
  onPick,
  inputId = "log-q",
  label = "Task or project",
}: TaskSearchProps) {
  const debounced = useDebouncedValue(query.trim(), 250);
  const search = useAssignable(debounced, SEARCH_LIMIT);
  const items = search.data?.items ?? [];
  const total = search.data?.total ?? 0;
  // A task reached through two assignments shows its demand so the two can be told apart.
  const seen = new Map<string, number>();
  for (const t of items) seen.set(t.projectTaskId, (seen.get(t.projectTaskId) ?? 0) + 1);

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        type="search"
        autoComplete="off"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search everything you're assigned to"
        className={TOUCH}
      />
      {search.isError ? (
        <p role="alert" className="px-2 py-3 text-sm text-danger">
          {search.error.message}
        </p>
      ) : search.isPending ? (
        <div className="space-y-1">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <div
          className={cn(
            "max-h-80 max-sm:max-h-[60dvh] divide-y overflow-y-auto rounded-md border",
            search.isPlaceholderData && "opacity-60",
          )}
        >
          {items.length === 0 && (
            <p className="px-3 py-3 text-sm text-muted-foreground">No matches</p>
          )}
          {items.map((task) => (
            <TaskOption
              key={task.key}
              task={task}
              showDemand={(seen.get(task.projectTaskId) ?? 0) > 1}
              onPick={onPick}
            />
          ))}
        </div>
      )}
      {total > items.length && (
        <p className="text-xs text-muted-foreground">
          Showing {items.length} of {total}
        </p>
      )}
    </div>
  );
}
